// ================================================================
// 答案顯示頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：顯示 Q&A 查詢結果、分析報告
// ================================================================

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  Divider,
  Chip,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Question, getCategoryColor, getCategoryName, getDynamicQuestionTitle, getQuestionDetail } from '../api/questions';
import { QAResponse, getQueryDetail } from '../api/qa';
import { formatBirthTime } from '../api/chart';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../hooks/useAuth';
import ReportContent from '../components/ReportContent';
import ShareCardModal, { extractSummary } from '../components/ShareCardModal';

/**
 * 答案頁面組件
 */
const AnswerPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { query_id } = useParams<{ query_id?: string }>();
  const { formatPrice } = useCurrency();
  const { getCurrentUser } = useAuth();

  // 從導航狀態取得問題和答案
  const [question, setQuestion] = useState<Question | null>(null);
  const [answer, setAnswer] = useState<QAResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isHistoryView, setIsHistoryView] = useState<boolean>(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      // 情況1：從導航狀態取得（新查詢）
      if (location.state) {
        const { question: q, answer: a } = location.state as any;
        setQuestion(q);
        setAnswer(a);
        setIsHistoryView(false);
        return;
      }

      // 情況2：從 URL 參數讀取歷史查詢
      if (query_id) {
        setLoading(true);
        setError('');
        try {
          const historyData = await getQueryDetail(parseInt(query_id));

          // 構造 Question 對象
          const q: Question = {
            question_id: historyData.question_id,
            question_text: historyData.question_text,
            category_id: historyData.question_id.substring(0, historyData.question_id.length - 3) as any,
            price: historyData.cost,
            is_free: historyData.cost === 0,
            function_name: '',
            description: '',
            tags: ''
          };

          // 補充 time_type 和 question_text_template（供 getDynamicQuestionTitle 正確顯示）
          try {
            const qDetail = await getQuestionDetail(historyData.question_id);
            if (qDetail.success && qDetail.question) {
              q.time_type = qDetail.question.time_type;
              q.question_text_template = qDetail.question.question_text_template;
            }
          } catch {
            // 取不到詳情時使用儲存的文字，不影響主功能
          }

          // 構造 Answer 對象
          const a: QAResponse = {
            success: true,
            question_id: historyData.question_id,
            question_text: historyData.question_text,
            birth_time: historyData.birth_time,
            gender: historyData.gender,
            output: historyData.answer_text || '',
            result: null
          };

          setQuestion(q);
          setAnswer(a);
          setIsHistoryView(true);
        } catch (err: any) {
          setError(err.message || '載入查詢記錄失敗');
        } finally {
          setLoading(false);
        }
        return;
      }

      // 情況3：沒有資料，返回問題列表
      navigate('/questions');
    };

    loadData();
  }, [location.state, query_id, navigate]);

  // 處理列印
  const handlePrint = () => {
    window.print();
  };

  // 處理分享
  const handleShare = () => {
    setShareModalOpen(true);
  };

  // 返回主頁並刷新用戶數據
  const handleBackToDashboard = async () => {
    try {
      // 刷新用戶數據以獲取最新餘額
      await getCurrentUser();
    } catch (error) {
      console.error('刷新用戶數據失敗:', error);
    } finally {
      // 無論刷新成功與否都導航回主頁
      navigate('/dashboard');
    }
  };

  // 載入中
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // 錯誤
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/history')}
        >
          返回查詢歷史
        </Button>
      </Container>
    );
  }

  if (!question || !answer) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* 成功提示（只在新查詢時顯示）*/}
      {!isHistoryView && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          查詢成功！已扣款 {question.is_free ? '免費' : formatPrice(question.price)}
        </Alert>
      )}

      {/* 歷史查詢提示 */}
      {isHistoryView && (
        <Alert severity="info" sx={{ mb: 3 }}>
          查看歷史查詢記錄
        </Alert>
      )}

      {/* 問題資訊 */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            查詢結果
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              列印
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<ShareIcon />}
              onClick={handleShare}
            >
              分享
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* 問題資訊 */}
        <Box sx={{ mb: 2 }}>
          <Chip
            label={getCategoryName(question.category_id)}
            sx={{
              backgroundColor: getCategoryColor(question.category_id),
              color: 'white',
              fontWeight: 'bold',
              mb: 1
            }}
          />

          <Typography variant="h6" gutterBottom>
            {getDynamicQuestionTitle(question)}
          </Typography>
        </Box>

        {/* 查詢資訊 */}
        <Card variant="outlined" sx={{ mb: 2, backgroundColor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              查詢資訊
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="body2">
                  <strong>出生時間：</strong>
                  {formatBirthTime(answer.birth_time)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2">
                  <strong>性別：</strong>
                  {answer.gender === 'Male' ? '男' : '女'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2">
                  <strong>查詢費用：</strong>
                  {question.is_free ? '免費' : formatPrice(question.price)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Paper>

      {/* 分析結果 */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoAwesomeIcon sx={{ color: '#667eea', mr: 1, fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#667eea' }}>
            分析報告
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* 美化的分析輸出 */}
        <ReportContent text={answer.output} />
      </Paper>

      {/* 操作按鈕 */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/questions')}
        >
          返回問題列表
        </Button>

        <Button
          variant="contained"
          onClick={handleBackToDashboard}
        >
          返回主頁
        </Button>

        <Button
          variant="outlined"
          onClick={() => navigate('/history')}
        >
          查看歷史記錄
        </Button>
      </Box>

      {/* 分享卡片 Modal */}
      {question && answer && (
        <ShareCardModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          questionText={question.question_text}
          summary={extractSummary(answer.output || '')}
          category={question.category_id}
        />
      )}
    </Container>
  );
};

export default AnswerPage;
